import type { CRBattle } from '@/types/qbot';

export type ModeBucket = 'ladder' | 'pathOfLegend' | 'challenge' | 'twoVtwo' | 'other';

export const MODE_ORDER: ModeBucket[] = ['ladder', 'pathOfLegend', 'challenge', 'twoVtwo', 'other'];

export function modeLabelKey(b: ModeBucket): string {
  return `battles.mode.${b}`;
}

/**
 * `battle.type` → bucket, calibrated against qCrC's live census: `distinct('type')`
 * over 120,437 real battles (May–July 2026) returned exactly twelve values, all
 * handled here. See `qCrC/docs/CLASH_ROYALE_EXPERTISE.md` §7.4.
 *
 * Keys are lower-cased because the wire values are camelCase (`PvP`,
 * `pathOfLegend`, `clanMate2v2`, `riverRacePvP`).
 *
 * `trail` is the one that matters most: it is Supercell's name for the
 * event/challenge battles (it carries an `eventTag`), and it is high volume —
 * second only to `pathOfLegend`. Substring guessing used to drop it into
 * `other`, hiding a big slice of the log behind the vaguest chip.
 *
 * The four omitted from the map — `friendly`, `clanMate`, `riverRacePvP`,
 * `riverRaceDuel`, `riverRaceDuelColosseum`, `boatBattle` — plus the rare
 * `unknown` fallback all resolve to `other` on purpose: friendlies and Clan War
 * are neither ladder nor challenge. Splitting Clan War into its own bucket is a
 * product call, not a calibration one.
 *
 * The enum is OPEN (Supercell adds modes), so anything unlisted falls through
 * to `other` rather than being guessed at.
 */
const TYPE_BUCKET: Readonly<Record<string, ModeBucket>> = {
  pvp: 'ladder', // Trophy Road ladder
  pathoflegend: 'pathOfLegend', // Ranked
  trail: 'challenge', // events / challenges (carries eventTag)
  tournament: 'challenge',
  clanmate2v2: 'twoVtwo',
};

/**
 * Map a battle to a coarse filter bucket.
 *
 * Resolution order matters:
 *  1. **Participant count decides 2v2.** The API contract is "1 entry in 1v1,
 *     2 in 2v2", which beats every string signal — `trail` events run as 2v2
 *     too (1,221 in the census), and their `type` says nothing about it.
 *  2. **Exact `type` lookup**, the authoritative field.
 *  3. **`gameMode.name` heuristics**, only to rescue a type we don't know
 *     (notably `unknown`, whose live cases are the CHAOS `Crazy_Arena` event
 *     and `Friendly`). Matched as substrings, never equality: the 36 live mode
 *     names carry seasonal affixes on both sides (`Ranked1v1_NewArena2`,
 *     `Ladder_CrownRush`, `TripleElixir_Ladder`).
 *
 * Never throws, never hides a battle.
 */
export function modeBucket(battle: CRBattle): ModeBucket {
  if ((battle.team?.length ?? 0) > 1 || (battle.opponent?.length ?? 0) > 1) return 'twoVtwo';

  const type = (battle.type ?? '').toLowerCase();
  const known = TYPE_BUCKET[type];
  if (known) return known;

  const mode = (battle.gameMode?.name ?? '').toLowerCase();
  if (mode.includes('teamvsteam') || mode.includes('2v2')) return 'twoVtwo';
  if (mode.includes('ranked')) return 'pathOfLegend';
  if (mode.includes('challenge') || mode.includes('crazy_arena') || mode.includes('draft'))
    return 'challenge';
  if (mode.includes('ladder')) return 'ladder';
  return 'other';
}
